import React, { useEffect, useMemo } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";

interface FieldForceFilters {
  sopStatus: string;
  priority: string;
  utilityService: string;
}

interface FieldForceFiltersProps {
  sopStatus: string;
  priority: string;
  utilityService: string;
  sopStatusList: Array<{ label: string; value: string | number }>;
  priorityList: Array<{ label: string; value: string | number }>;
  utilityServicesData: any[]; // The raw data mapped internally
  onApplyFilters: (filters: FieldForceFilters) => void;
  onResetFilters: () => void;
}

export const FieldForceFiltersPanel: React.FC<FieldForceFiltersProps> = ({
  sopStatus,
  priority,
  utilityService,
  sopStatusList,
  priorityList,
  utilityServicesData,
  onApplyFilters,
  onResetFilters,
}) => {
  // Transform lists to options format
  const sopStatusOptions = useMemo(
    () =>
      sopStatusList?.map((status) => ({
        value: status.value,
        label: status.label,
      })) || [],
    [sopStatusList],
  );

  const priorityOptions = useMemo(
    () =>
      priorityList?.map((p) => ({
        value: p.value,
        label: p.label,
      })) || [],
    [priorityList]
  );
  
  const utilityServiceOptions = useMemo(() => {
    return (
      utilityServicesData?.map((service: any) => ({
        value: service.name,
        label: service.name,
      })) || []
    );
  }, [utilityServicesData]);

  // Define filter fields
  const filterFields: FormField[] = useMemo(
    () => [
      {
        name: "sopStatus",
        label: "SOP Status",
        type: "select",
        placeholder: "Select Status",
        options: sopStatusOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
         name: "utilityService",
         type: "select",
         label: "Utility Service",
         placeholder: "Select utility service",
         required: false,
         options: utilityServiceOptions,
         className: "col-span-1",
         classes: {
          container: "w-full mb-6",
         },
      },
      {
        name: "priority",
        label: "Priority",
        type: "select",
        placeholder: "Select Priority",
        options: priorityOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
    ],
    [sopStatusOptions, priorityOptions, utilityServiceOptions]
  );

  // Create form instance
  const form = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Sync form values when URL params change
  useEffect(() => {
    form.reset({
      sopStatus: decodeURIComponent(sopStatus) || "",
      priority: decodeURIComponent(priority) || "",
      utilityService: decodeURIComponent(utilityService) || "",
    });
  }, [sopStatus, priority, utilityService, form]);

  const handleFilterSubmit = () => {
    const formValues = form.getValues();
    onApplyFilters({
      sopStatus: formValues.sopStatus || "",
      priority: formValues.priority || "",
      utilityService: formValues.utilityService || ""
    });
  };

  const handleFilterReset = () => {
    form.reset({
      sopStatus: "",
      priority: "",
      utilityService: "",
    });
    onResetFilters();
  };

  const handleFieldChange = (field: string, value: string) => {
    // Add any field-specific logic here if needed
  };

  return (
    <div>
      <DynamicForm
        fields={filterFields}
        form={form}
        onSubmit={handleFilterSubmit}
        onReset={handleFilterReset}
        onFieldChange={handleFieldChange}
        gridColumns={3}
        config={{
          layout: "vertical",
          showSubmitButton: true,
          showResetButton: true,
          submitButtonText: "Apply Filters",
          resetButtonText: "Clear",
          classes: {
            buttonContainer: "flex justify-end gap-4",
          },
        }}
      />
    </div>
  );
};
